// WHIT-497: Hungarian names for the package-tour reference data. allmylespy
// names its vocabulary in English only and leaves translation to us
// (tour_vocabulary.py), and the provider names countries in English too, so
// the names are made here: vocabulary codes through config/tour-vocabulary.json
// and t(), countries from their ISO code (the country level's id) through
// Intl.DisplayNames. Region and resort names stay as the provider sends them.
// Pure (no DOM, no React) so `node --test web/utils/tourNames.test.js` can
// load it.

const vocabulary = require('../config/tour-vocabulary.json');

const labels = list =>
  new Map(vocabulary[list].map(({ code, label }) => [code, label]));

const LISTS = {
  boardTypes: labels('boardTypes'),
  roomTypes: labels('roomTypes'),
  amenities: labels('amenities'),
};
const CITIES = labels('departureCities');

// WHIT-505: CLDR's English name for TR is now "Türkiye", which reads as
// foreign text on an English page; the English customer knows it as Turkey.
const OVERRIDES = { en: { TR: 'Turkey' } };

// The country's name in `locale`; null when the code is not a country code:
// Intl throws on a region or resort id and echoes an unknown code.
const countryName = (code, locale) => {
  const override = OVERRIDES[locale] && OVERRIDES[locale][code];
  if (override) return override;
  try {
    const name = new Intl.DisplayNames([locale], { type: 'region' }).of(code);
    return name && name !== code ? name : null;
  } catch {
    return null;
  }
};

// A code's name through t(); a code the config does not know keeps the API's.
const named = (list, t) => entry => {
  const label = list.get(entry.code);
  return label ? { ...entry, name: t(label) } : entry;
};

const byName = locale => (a, b) => a.name.localeCompare(b.name, locale);

// Countries by their ISO code, sorted again in the visitor's language; regions
// and resorts unchanged.
const localizeDestinations = (destinations, locale) =>
  destinations &&
  destinations
    .map(node =>
      node.level === 'country'
        ? { ...node, name: countryName(node.id, locale) || node.name }
        : node,
    )
    .sort(byName(locale));

const localizeReference = (reference, t, locale) => {
  if (!reference) return reference;
  const list = (key, map) =>
    reference[key] ? reference[key].map(named(map, t)) : reference[key];
  // The hotel results name a hotel's country in the provider's English, not
  // by code: the reference countries are the same provider's names.
  const countryNames = {};
  for (const node of reference.destinations || []) {
    const name = node.level === 'country' && countryName(node.id, locale);
    if (name) countryNames[node.name] = name;
  }
  return {
    ...reference,
    destinations: localizeDestinations(reference.destinations, locale),
    departureAirports:
      reference.departureAirports &&
      reference.departureAirports.map(airport => {
        const city = CITIES.get(airport.iata || airport.id);
        return city ? { ...airport, name: t(city) } : airport;
      }),
    boardTypes: list('boardTypes', LISTS.boardTypes),
    roomTypes: list('roomTypes', LISTS.roomTypes),
    amenities: list('amenities', LISTS.amenities),
    countryNames,
  };
};

// A departure city's name through t(), for lists that know the IATA code.
const departureCityName = (iata, t) => {
  const city = CITIES.get(iata);
  return city ? t(city) : null;
};

// WHIT-505: a room as our own words: its type's name and, when the offer has
// one, the view ("Double room, Sea view"). The tour operator's own room name
// ("Doppelzimmer Meerblick") is free text in its own language, so the pages
// never show it. The view codes sit in the roomTypes list of the vocabulary.
const roomViewName = (code, t) => {
  const label = code && LISTS.roomTypes.get(code);
  return label ? t(label) : null;
};
const roomLabel = (typeName, view, t) =>
  [typeName, roomViewName(view, t)].filter(Boolean).join(', ');

// WHIT-499: the star and guest-rating filter options come with the provider's
// English names ("5 stars", "90% or better"); their ids are the numbers, so
// the names are made from those. Anything else keeps the name it came with.
const facetOptionName = (code, option, t) => {
  const value = Number(option.id);
  if (!option.id || !Number.isFinite(value)) return option.name;
  if (code === 'category') return t('{{count}} stars', { count: value });
  if (code === 'guestRating')
    return t('At least {{percent}}% guest recommendation', { percent: value });
  return option.name;
};

module.exports = {
  facetOptionName,
  countryName,
  departureCityName,
  localizeDestinations,
  localizeReference,
  roomLabel,
};
